import { useEffect, useState } from "react";
import {
  Connector,
  ErrorRemediationScreenEnum,
  Integration,
  IntegrationSetupStepPathType,
  LinkingFlowSetupResult,
  NextPagePayloadInfo,
  ValidationErrors,
  ValidationSuccess
} from "../../../config/types";
import { passValidationErrors } from "../../../utils/postMessageUtils";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import { getIsCurrentStepSetScraperStepPath } from "../connector-setup/Helpers";
import ErrorRemediationInitialInterstitial from "../error-remediation/ErrorRemediationInitialInterstitial";
import ErrorScreen from "../error-remediation/ErrorScreen";

type Props = {
  linkingFlowPayload: NextPagePayloadInfo;
};

const ErrorRemediationScreensContainer = ({ linkingFlowPayload }: Props) => {
  const alreadyValidatedFlow = linkingFlowPayload.already_validated_flow ?? false;
  // in the future we can set the start to the second screen for already integrated menu
  const {
    translate,
    getNextPageAndSetPage,
    navigateToPreviousEntryInHistory,
    onExit,
    setLinkedAccountID,
  } = useAgentHandlerLinkModalContext();

  const [currentErrorRemediationScreen, setCurrentErrorRemediationScreen] =
    useState<ErrorRemediationScreenEnum>(
      alreadyValidatedFlow
        ? ErrorRemediationScreenEnum.VALIDATION_ERRORS
        : ErrorRemediationScreenEnum.INITIAL_INTERSTITIAL
    );

  const validationSuccesses: ValidationSuccess[] =
    alreadyValidatedFlow
      ? linkingFlowPayload.validation_successes ?? []
      : linkingFlowPayload.validation_successes ?? [];
  const validationErrors: ValidationErrors =
    alreadyValidatedFlow
      ? linkingFlowPayload.validation_errors ?? []
      : linkingFlowPayload.validation_errors ?? [];
  const validationResult: LinkingFlowSetupResult =
    alreadyValidatedFlow
      ? linkingFlowPayload.validation_result ?? LinkingFlowSetupResult.FIX_INPUT
      : linkingFlowPayload.validation_result ?? LinkingFlowSetupResult.FIX_INPUT;
  const integration: Integration | undefined = linkingFlowPayload.integration;
  const linkedAccountFormData = linkingFlowPayload.linked_account_form_data;
  const registeredUserFormData = linkingFlowPayload.registered_user_form_data;
  const connector: Connector | undefined = linkingFlowPayload.connector;
  const isScraperStep = getIsCurrentStepSetScraperStepPath(
    integration?.step_config?.initial_setup_set ?? IntegrationSetupStepPathType.MANUAL_LINKING
  );
  const organization = linkingFlowPayload.organization;

  const isSuccessfulFlow = validationResult === LinkingFlowSetupResult.SUCCESS;
  const isPermissionsError = validationResult === LinkingFlowSetupResult.MISSING_ADMIN_PERMISSION;
  const isBlockingValidationError = validationResult === LinkingFlowSetupResult.VALIDATION_BP_ERROR;
  const canShowBPError =
    isBlockingValidationError && validationErrors.length > 0 && "detail" in validationErrors[0];

  // Check if this is credential validation (no linked account yet) vs post-linking validation
  const isCredentialValidation = !linkingFlowPayload?.linked_account?.id || 
    linkingFlowPayload.linked_account.id === "00000000-0000-0000-0000-000000000000";

  useEffect(() => {
    if (!isSuccessfulFlow) {
      const errorsToPass =
        validationErrors && validationErrors.length > 0
          ? validationErrors
          : ([
              // Not translating this since its being sent to our customers instead of end users
              {
                detail: `The provided ${integration?.name} credentials for end user ${linkingFlowPayload?.linked_account?.end_user_organization_name} were invalid. ( End User Origin ID: ${linkingFlowPayload?.linked_account?.end_user.origin_id}, Linked Account ID: ${linkingFlowPayload?.linked_account?.id} ) `,
                problem_type: "FIX_INPUT",
              },
            ] as ValidationErrors);
      passValidationErrors(errorsToPass);
    }
  }, [validationErrors]);

  const buttonOnNext = isBlockingValidationError
    ? () => {
        getNextPageAndSetPage({
          selected_connector: linkingFlowPayload?.connector,
        });
      }
    : () => setCurrentErrorRemediationScreen(ErrorRemediationScreenEnum.VALIDATION_ERRORS);

  // Only set linked account ID if we have a real linked account (not credential validation)
  if (linkingFlowPayload.linked_account?.id && !isCredentialValidation) {
    setLinkedAccountID(linkingFlowPayload.linked_account.id);
  }

  // Scrapers can also raise permissions issues but these are blocking and will not have any successful fields/models
  // For these we want to show a failed workflow/like we do for other errors
  const hasCommonModels =
    (validationErrors ?? []).find(
      (error) => error?.common_model != null && error?.common_model !== ""
    ) != null;
  const hasSuccesses = validationSuccesses && validationSuccesses.length > 0;
  const hasValidationErrors = validationErrors && validationErrors.length > 0;
  const isSuccess = hasSuccesses && isSuccessfulFlow;

  const RenderErrorRemediationScreens = () => {
    switch (currentErrorRemediationScreen) {
      case ErrorRemediationScreenEnum.INITIAL_INTERSTITIAL:
        return (
          <ErrorRemediationInitialInterstitial
            connector={connector}
            // we want the successful flow to look like a permissions error
            // show them the green checkmark however it will skip to the already integrated screen on button click
            isPermissionsError={
              (isPermissionsError && hasCommonModels && hasSuccesses) || isSuccessfulFlow
            }
            onBack={() => navigateToPreviousEntryInHistory(linkedAccountFormData, registeredUserFormData)}
            onExit={onExit}
            onNext={buttonOnNext}
            translate={translate}
            // the below fields allow us to dispaly bp validation errors and relink from the error screen
            subtext={canShowBPError ? validationErrors[0]["detail"] : undefined}
            isBlockingValidationError={isBlockingValidationError}
          />
        );
      case ErrorRemediationScreenEnum.VALIDATION_ERRORS:
      default:
        return hasValidationErrors || isSuccess ? (
          <ErrorScreen
            customText={""}
            validationErrors={validationErrors}
            validationSuccesses={validationSuccesses}
            integration={integration}
            connector={connector}
            organization={organization}
            alreadyValidatedFlow={alreadyValidatedFlow}
            isPermissionsIssue={isPermissionsError && hasCommonModels && hasSuccesses}
            isErrorRemediated
            isSuccessfulFlow={isSuccessfulFlow}
            skipWithoutValidating={() =>
              getNextPageAndSetPage({
                selected_connector: connector,
              })
            }
            onClickBottomAction={() =>
              getNextPageAndSetPage({
                selected_connector: connector,
              })
            }
            isScraperStep={isScraperStep!}
            // removing navigation for permissions issues since users can skipWithoutValidating and get led to custom field mappings pages
            onClickBack={() =>
              alreadyValidatedFlow || (!isSuccess && hasValidationErrors)
                ? navigateToPreviousEntryInHistory(linkedAccountFormData, registeredUserFormData)
                : setCurrentErrorRemediationScreen(ErrorRemediationScreenEnum.INITIAL_INTERSTITIAL)
            }
            onClickNext={() => {
              getNextPageAndSetPage({
                selected_connector: connector,
              })
            }}
            onExit={onExit}
          />
        ) : (
          <ErrorScreen
            validationErrors={validationErrors}
            integration={integration}
            isScraperStep={isScraperStep!}
            organization={organization}
            isErrorRemediated
            onClickBottomAction={() =>
              setCurrentErrorRemediationScreen(ErrorRemediationScreenEnum.INITIAL_INTERSTITIAL)
            }
            onClickBack={() => navigateToPreviousEntryInHistory(linkedAccountFormData, registeredUserFormData)}
            onExit={onExit}
          />
        );
    }
  };

  return <RenderErrorRemediationScreens />;
};

export default ErrorRemediationScreensContainer;
